import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import $ from "jquery";
import { observable, runInAction } from "mobx";
import { EEcommerceSystemEnvironment } from "@devowl-wp/api-real-commerce";
import { Transaction } from "./transaction.js";
import { LOCAL_STORAGE_AFFILIATE_NAME } from "../others/createAffiliateStorageFromParams.js";
import { CheckoutStep } from "../store/checkoutV1.js";
import { localStorageTest } from "../utils/localStorageTest.js";
const WAIT_PULL_COMPLETE_ORDER_INTERVAL = 5000;
class PaddleTransaction extends Transaction {
    /**
     * @see https://developer.paddle.com/guides/how-tos/checkout/inline-checkout
     */ get Paddle() {
        return this.paddle;
    }
    async loadVendorScripts() {
        // Delete affiliate cookies when coming through a recovery email
        if (this.isRecovered) {
            if (localStorageTest()) {
                localStorage.removeItem(LOCAL_STORAGE_AFFILIATE_NAME);
            }
        }
        // Load Paddle API once
        await $.getScript("https://cdn.paddle.com/paddle/paddle.js");
        this.paddle = window.Paddle;
        if (this.product.data.productPlan.product.ecommerceSystem.environment === EEcommerceSystemEnvironment.Sandbox) {
            this.paddle.Environment.set("sandbox");
        }
        this.paddle.Setup({
            vendor: +this.product.data.productPlan.product.ecommerceSystem.vendorId,
            eventCallback: this.eventCallback.bind(this)
        });
    }
    eventCallback(data) {
        this.waitCompletedOrder(data);
        this.updatePrices(data);
        const { event } = data;
        runInAction(()=>{
            if (event === "Checkout.Vat.Applied") {
                this.hasAddedVat = true;
            } else if (event === "Checkout.Vat.Remove") {
                this.hasAddedVat = false;
                this.removePersistedVat();
            }
        });
    }
    /**
     * Create an interval to check for complete order (this does not mean that the payment is
     * complete, for this checkout `Transaction#applyPayment`!).
     *
     * **Why not using `successCallback` from Paddle SDK?** We had serious issues for some customers
     * as the callback got not called correctly. Paddle Support promised us that there is no known
     * similar case known by them. For more information see ticket `CU-hh8y3c`.
     */ waitCompletedOrder({ event, eventData }) {
        // Short circuit: Is it already awaiting payment?
        if (this.store.step >= CheckoutStep.AwaitingPayment) {
            this.disableWaitCompleteOrderInterval();
            return;
        }
        // If we reach the needed event / step AND we are not already pulling the status
        if (event === "Checkout.PaymentMethodSelected" && !this.waitCompleteOrderPullTimeout) {
            this.waitCompleteOrderPullTimeout = setInterval(async ()=>{
                if (document.visibilityState === "hidden") {
                    return;
                }
                this.currentlyInIntervalExecution = true;
                try {
                    const { checkout: { ecommerceSystemCheckoutId } } = await this.fetchIsIncompleteCheckoutComplete();
                    if (ecommerceSystemCheckoutId) {
                        this.disableWaitCompleteOrderInterval();
                        this.applyPayment(ecommerceSystemCheckoutId);
                    }
                } catch (e) {
                    this.error = e;
                } finally{
                    this.currentlyInIntervalExecution = false;
                }
            }, WAIT_PULL_COMPLETE_ORDER_INTERVAL);
        }
        // User did cancel the payment, lets disable pulling
        if (event === "Checkout.PaymentMethodChange") {
            this.disableWaitCompleteOrderInterval();
        }
        // Payment got completed, lets disable pulling and directly apply payment
        if (event === "Checkout.PaymentComplete" && !this.currentlyInIntervalExecution) {
            this.disableWaitCompleteOrderInterval();
            this.applyPayment(eventData.checkout.id);
        }
    }
    /**
     * Listen to all pricing changes including coupon-changes.
     *
     * @see https://developer.paddle.com/guides/how-tos/checkout/inline-checkout#updating-in-page-information
     */ updatePrices(data) {
        try {
            const { coupon, prices: { customer }, recurring_prices } = data.eventData.checkout;
            this.applyNewPrices({
                initialPrice: +customer.total,
                initialPriceTax: +customer.total_tax,
                recurringPrice: recurring_prices ? +recurring_prices.customer.total : undefined,
                recurringPriceTax: recurring_prices ? +recurring_prices.customer.total_tax : undefined,
                coupon: coupon.coupon_code
            });
        } catch (e) {
            // Currently, we do ignore backend errors
            console.log(e);
        }
    }
    disableWaitCompleteOrderInterval() {
        clearInterval(this.waitCompleteOrderPullTimeout);
        this.waitCompleteOrderPullTimeout = undefined;
    }
    constructor(...args){
        super(...args), this.hasAddedVat = false, /**
     * Avoid race condition with pulling the status and the Paddle "complete" event.
     */ this.currentlyInIntervalExecution = false;
    }
}
_ts_decorate([
    observable
], PaddleTransaction.prototype, "hasAddedVat", void 0);
export { PaddleTransaction };
